import { useCallback, useEffect, useMemo, useState } from "react";
import { useIndexDb } from "modules/providers/index-db-provider";
import { useLiveQuery } from "dexie-react-hooks";
import {
  getDistributionGroupIdListFromPlainText,
  getFileExtension,
  getGifInput,
  getMentionIdsFromText,
  getTagIdListFromPlainText,
  getUserIdListFromPlainText,
} from "modules/utils/Functions";
import moment from "moment";
import {
  DRAFT_TYPE,
  FILE_STATE_TYPE,
  IFileInTransit,
  IMessageThreadDraft,
  ITask,
} from "modules/providers/index-db-provider/index-db-models";
import { TenorImage } from "gif-picker-react";
import { useUsers } from "modules/providers/users-provider";
import { Option } from "modules/selectors/user-tag-group-selector";
import {
  FileFragment,
  GifInput,
  MessageFragment,
  MessageThreadFragment,
  MessageThreadType,
  MessageType,
  TaskPriorityType,
  TaskStatusType,
} from "gql/graphql";
import ObjectID from "bson-objectid";
import { NEXT_PUBLIC_CLOUDFRONT_URL } from "app/client";
import { isEmpty } from "lodash";
import {
  FORWARD_DRAFT_ID,
  MENTION_TYPE,
  TASK_DRAFT_ID,
} from "modules/utils/Constants";
import DeltaBuilder from "modules/quill/delta-builder";

export type BottomButtonType =
  | "tag"
  | "mention"
  | "emoji"
  | "file"
  | "gif"
  | "external-user"
  | "send-thread"
  | "send-chat"
  | "send-task"
  | "create-task";

interface INewDraftProps {
  type: MessageThreadType;
  draftType: DRAFT_TYPE;
  userIds?: string[];
  distributionGroupIds?: string[];
  tagIdList?: string[];
  task?: ITask;
}

interface IProps {
  draftId: string;
}

const useMessageThreadDraft = ({ draftId }: IProps) => {
  const { usersById } = useUsers();
  const { db } = useIndexDb();

  const draft = useLiveQuery(
    () => db.draft.where("id").equals(draftId).first(),
    [draftId],
    null,
  );

  // TEMP hotfix to ensure all drafts have a messageId or messageThreadId
  // Migration perhaps?
  useEffect(() => {
    (async () => {
      await db.transaction("rw", db.draft, async () => {
        const drafts = await db.draft
          .filter((d) => d.messageId == null || d.messageThreadId == null)
          .toArray();

        if (drafts.length === 0) return;

        await db.draft.bulkUpdate(
          drafts.map((d) => ({
            key: d.id,
            changes: {
              ...(d.messageId == null && {
                messageId: new ObjectID().toHexString(),
              }),
              ...(d.messageThreadId == null && {
                messageThreadId: new ObjectID().toHexString(),
              }),
            },
          })),
        );
      });
    })();
  }, [db]);
  //END TEMP hotfix

  const deleteDraft = useCallback(async () => {
    await db.file.where("draftId").equals(draftId).delete();
    await db.draft.where("id").equals(draftId).delete();
  }, [db.draft, db.file, draftId]);

  const createDraft = useCallback(
    async (
      {
        type,
        draftType,
        userIds,
        distributionGroupIds,
        tagIdList,
        task,
      }: INewDraftProps,
      overwriteExisting = false,
    ) => {
      if (!overwriteExisting) {
        const existingDraft = await db.draft
          .where("id")
          .equals(draftId)
          .first();
        if (existingDraft) {
          return;
        }
      }

      const delta = new DeltaBuilder();
      userIds?.forEach((userId) => {
        delta.mention(userId, MENTION_TYPE.USER);
      });

      distributionGroupIds?.forEach((groupId) => {
        delta.mention(groupId, MENTION_TYPE.DISTRIBUTION_GROUP);
      });

      tagIdList?.forEach((tagId) => {
        delta.mention(tagId, MENTION_TYPE.TAG);
      });

      // If the draft is a task, ensure it has a task object with default values
      const _task: ITask | undefined =
        type === MessageThreadType.Task
          ? (task ?? {
              priorityType: TaskPriorityType.None,
              statusType: TaskStatusType.NotStarted,
            })
          : undefined;

      const _draft: IMessageThreadDraft = {
        id: draftId,
        draftType,
        messageThreadId: new ObjectID().toHexString(),
        messageId: new ObjectID().toHexString(),
        updateTime: moment().toISOString(),
        text: "",
        delta: delta.build(),
        distributionGroupIdList: distributionGroupIds || [],
        calloutUserIdList: userIds || [],
        tagIdList: tagIdList || [],
        gifList: [],
        quotedMessageList: [],
        relatedMessageThread: null,
        subject: "",
        isLocked: false,
        isExternal: false,
        type,
        task: _task,
      };
      await db.file.where("draftId").equals(draftId).delete();
      await db.draft.put(_draft);
    },
    [db.draft, db.file, draftId],
  );

  const files = useLiveQuery(
    () => db.file.where("draftId").equals(draftId).toArray() ?? [],
    [db, draftId],
  );

  const isUploadingFiles = useMemo(() => {
    if (!files) return false;
    return files.some((file) => file.stateType !== FILE_STATE_TYPE.COMPLETE);
  }, [files]);

  const hasAttachments = useMemo(
    () => (draft?.gifList.length || 0) > 0 || (files?.length || 0) > 0,
    [draft?.gifList.length, files?.length],
  );

  const mentionIds: string[] = useMemo(() => {
    if (!draft) {
      return [];
    }
    return [
      ...new Set([
        ...getMentionIdsFromText(draft.text),
        ...(draft.type === MessageThreadType.Chat
          ? draft.calloutUserIdList
          : []),
      ]),
    ];
  }, [draft]);

  const isExternal = draft?.isExternal;

  const isMessageReplyValid = useMemo(() => {
    if (!draft) return false;
    return (
      (draft.text && draft.text.length > 0 && !isEmpty(draft.text.trim())) ||
      draft.gifList?.length > 0 ||
      (files ?? []).length > 0 ||
      (draft.quotedMessageList?.length || 0) > 0
    );
  }, [draft, files]);

  const isNewMessageThreadValid = useMemo(() => {
    if (!draft) return false;
    // if (!isMessageReplyValid) return false;
    switch (draft.type) {
      case MessageThreadType.Chat:
        return draft.calloutUserIdList.length > 0;
      case MessageThreadType.Task:
        return (draft.subject?.length ?? 0) > 0;
      default:
        return (
          draft.calloutUserIdList.length > 0 ||
          draft.distributionGroupIdList.length > 0
        );
    }
  }, [draft]);

  const setSubject = useCallback(
    (subject: string) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.subject = subject;
        }),
    [db.draft, draftId],
  );

  const setType = useCallback(
    (type: MessageThreadType) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.type = type;
          draft.task =
            type === MessageThreadType.Task
              ? {
                  priorityType: TaskPriorityType.None,
                  statusType: TaskStatusType.NotStarted,
                }
              : undefined;
        }),
    [db.draft, draftId],
  );

  const setTask = useCallback(
    (task: ITask) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.task = task;
        }),
    [db.draft, draftId],
  );

  const setHappycoWorkOrderId = useCallback(
    (happycoWorkOrderId: string) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.happycoWorkOrderId = happycoWorkOrderId;
        }),
    [db.draft, draftId],
  );

  const userValues = useMemo<Option[]>(() => {
    if (!draft) return [];
    return draft.calloutUserIdList.map((userId) => ({
      id: userId,
      type: "user",
      active: usersById[userId]?.active ?? false,
    }));
  }, [draft, usersById]);

  const addGif = useCallback(
    async (tenorImage: TenorImage) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.gifList.push(getGifInput(tenorImage));
        }),
    [db.draft, draftId],
  );

  const deleteGif = useCallback(
    async (id: string) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.gifList = draft.gifList.filter((gif) => gif.id !== id);
        }),
    [db.draft, draftId],
  );

  const updateMessage = useCallback(
    async (delta: string, plainText: string) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.text = plainText;
          draft.delta = delta;
          draft.calloutUserIdList = [
            ...new Set([
              ...getUserIdListFromPlainText(plainText),
              ...(draft.type === MessageThreadType.Chat
                ? draft.calloutUserIdList
                : []),
            ]),
          ];
          draft.distributionGroupIdList =
            getDistributionGroupIdListFromPlainText(plainText);
          draft.tagIdList = getTagIdListFromPlainText(plainText);
        }),
    [db.draft, draftId],
  );

  const toggleRelatedMessageThread = useCallback(() => {
    if (draft && draft.relatedMessageThread) {
      return db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          if (draft.relatedMessageThread) {
            draft.updateTime = moment().toISOString();
            draft.relatedMessageThread.isRemoved =
              !draft.relatedMessageThread.isRemoved;
          }
        });
    }
    return undefined;
  }, [draft, db.draft, draftId]);

  const setRelatedMessageThread = useCallback(
    (messageThread: MessageThreadFragment) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.relatedMessageThread = {
            ...messageThread,
            isRemoved: false,
          };
        }),
    [db.draft, draftId],
  );

  const clearQuotedMessage = useCallback(
    async (quotedMessageId: string) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.quotedMessageList = draft.quotedMessageList?.filter(
            (m) => m.messageId !== quotedMessageId,
          );
        }),
    [db.draft, draftId],
  );

  const setQuotedMessages = useCallback(
    (...messages: MessageFragment[]) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.quotedMessageList = [];
          messages.forEach((message) => {
            const isEmailMessage = message.type === MessageType.Email;
            const text = isEmailMessage
              ? "Attached email message"
              : message.text;
            const file = isEmailMessage ? message.htmlPdfFile : undefined;
            draft.quotedMessageList?.push({
              id: new ObjectID().toHexString(),
              createdUserId: message.createdUserId,
              createTime: message.createTime,
              messageId: message.id,
              messageThreadId: message.messageThreadId,
              parentMessageThreadId: "",
              parentMessageId: "",
              text,
              quillDelta: message.quillDelta,
              actionText: message.actionText,
              locale: message.locale,
              localizedText: message.localizedText,
              canAccessMessageThread: true,
              file,
            });
          });
        }),
    [db.draft, draftId],
  );

  const setReplyQuotedMessage = useCallback(
    (message: MessageFragment, file?: FileFragment) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.quotedMessageList = [
            {
              id: new ObjectID().toHexString(),
              createdUserId: message.createdUserId,
              createTime: message.createTime,
              messageId: message.id,
              messageThreadId: message.messageThreadId,
              parentMessageThreadId: "",
              parentMessageId: "",
              text:
                message.type === MessageType.User ? message.text : undefined,
              actionText: message.actionText,
              locale: message.locale,
              localizedText:
                message.type === MessageType.User
                  ? message.localizedText
                  : undefined,
              canAccessMessageThread: true,
              file,
            },
          ];
        }),
    [db.draft, draftId],
  );

  const updateCalloutUserIds = useCallback(
    (userIds: string[]) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.calloutUserIdList = userIds;
        }),
    [db.draft, draftId],
  );

  const toggleLocked = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) =>
      db.draft.where("id").equals(draftId).modify({
        updateTime: moment().toISOString(),
        isLocked: e.target.checked,
      }),
    [db.draft, draftId],
  );

  const setIsLocked = useCallback(
    async (isLocked: boolean) =>
      db.draft.where("id").equals(draftId).modify({
        updateTime: moment().toISOString(),
        isLocked,
      }),
    [db.draft, draftId],
  );

  const setCreateMessageUserIdList = useCallback(
    async (createMessageUserIdList: string[]) =>
      db.draft.where("id").equals(draftId).modify({
        updateTime: moment().toISOString(),
        createMessageUserIdList,
      }),
    [db.draft, draftId],
  );

  const setForwardToMessageThread = useCallback(
    (messageThread?: MessageThreadFragment) =>
      db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.forwardToMessageThread = messageThread;
          draft.type = messageThread ? messageThread.type : draft.type;
          draft.draftType = !messageThread ? "new-thread" : "reply";
        }),
    [db.draft, draftId],
  );

  const copyFiles = useCallback(
    (
      ..._files: {
        byteCount: number;
        name: string;
        contentType: string;
        key: string;
        previewKey?: string | null;
        previewCloudFrontUrl?: string | null;
        isCompressed: boolean;
        isVoiceNote: boolean;
      }[]
    ) => {
      const filesToAdd: IFileInTransit[] = _files
        .map(
          (file): IFileInTransit => ({
            id: new ObjectID().toHexString(),
            updateTime: moment().toISOString(),
            draftId,
            byteCount: file.byteCount ?? 0,
            uploadedByteCount: file.byteCount ?? 0,
            percentage: 100,
            name: file.name,
            stateType: FILE_STATE_TYPE.COMPLETE,
            contentType: file.contentType,
            previewCloudFrontUrl: file.previewCloudFrontUrl,
            extension: getFileExtension(file.name),
            url: `${NEXT_PUBLIC_CLOUDFRONT_URL}/${file.key}`,
            isCopied: true,
            key: file.key,
            previewKey: file.previewKey,
            isCompressed: file.isCompressed,
            isVoiceNote: file.isVoiceNote,
          }),
        )
        //filter to make sure the files weren't already added
        .filter((file) => !(files ?? []).some((f) => f.id === file.id));

      return db.file.bulkAdd(filesToAdd);
    },
    [db.file, draftId, files],
  );

  const setEditMessageDraft = useCallback(
    (message: MessageFragment) => {
      const now = moment().toISOString();

      return Promise.all([
        db.draft
          .where("id")
          .equals(draftId)
          .modify((draft) => {
            draft.updateTime = now;
            draft.text = message.text ?? "";
            draft.delta = message.quillDelta ?? null;
            draft.gifList =
              message.gifList?.map(
                (gif) =>
                  ({
                    id: gif.id,
                    url: gif.url,
                    name: gif.name,
                    imageUrl: gif.imageUrl,
                    videoUrl: gif.videoUrl,
                    height: gif.height,
                    width: gif.width,
                  }) as GifInput,
              ) ?? [];
          }),

        db.file.bulkPut(
          message.fileList.map((file) => ({
            id: file.id,
            updateTime: now,
            draftId,
            byteCount: file.byteCount,
            uploadedByteCount: file.byteCount,
            percentage: 100,
            name: file.name,
            stateType: FILE_STATE_TYPE.COMPLETE,
            contentType: file.contentType,
            extension: getFileExtension(file.name),
            isCopied: true,
            key: file.key,
            previewKey: file.previewKey,
            previewCloudFrontUrl: file.previewCloudFrontUrl,
            isCompressed: file.isCompressed,
            isVoiceNote: file.isVoiceNote,
            url: `${NEXT_PUBLIC_CLOUDFRONT_URL}/${file.key}`,
          })),
        ),
      ]);
    },
    [db.draft, db.file, draftId],
  );

  const setDelta = useCallback(
    async (builder: DeltaBuilder) => {
      await db.draft
        .where("id")
        .equals(draftId)
        .modify((draft) => {
          draft.updateTime = moment().toISOString();
          draft.text = builder.toPlainText();
          draft.delta = builder.build();
        });
    },
    [db.draft, draftId],
  );

  const taskButtonType = useMemo(() => {
    if (
      draft?.type === MessageThreadType.Task &&
      draft.forwardToMessageThread
    ) {
      return "send-task";
    }
    return [FORWARD_DRAFT_ID, TASK_DRAFT_ID].includes(draftId)
      ? "create-task"
      : "send-task";
  }, [draft?.forwardToMessageThread, draft?.type, draftId]);

  const bottomButtonTypes = useMemo((): BottomButtonType[] => {
    //https://teamcake.atlassian.net/browse/SYN-1843
    //Implements a smoother transition when a draft is not loaded yet

    if (draft?.draftType === "edit-message") {
      const editMessgeButtonTypes: BottomButtonType[] = [
        "emoji",
        "file",
        "gif",
      ];
      switch (draft?.type) {
        case MessageThreadType.Thread:
          return [...editMessgeButtonTypes, "send-thread"];
        case MessageThreadType.Chat:
          return [...editMessgeButtonTypes, "send-chat"];
        case MessageThreadType.Task:
          return [...editMessgeButtonTypes, "send-task"];
        default:
          return editMessgeButtonTypes;
      }
    }

    switch (draft?.type) {
      case MessageThreadType.Thread:
        return [
          "tag",
          "mention",
          "emoji",
          "file",
          "gif",
          "external-user",
          "send-thread",
        ];
      case MessageThreadType.Chat:
        return ["mention", "file", "gif", "emoji", "send-chat"];
      case MessageThreadType.Task:
        return [
          "tag",
          "mention",
          "external-user",
          "file",
          "gif",
          "emoji",
          taskButtonType,
        ];
      default:
        return ["mention", "file", "gif", "emoji"];
    }
  }, [draft?.type, draft?.draftType, taskButtonType]);

  const [error, setError] = useState<Error>();

  const isDraftLoaded = useMemo(
    () => !!draft && draftId === draft.id,
    [draft, draftId],
  );

  const canSendMessage = useMemo(() => {
    if (isUploadingFiles) return false;
    if (draft?.draftType === "new-thread") {
      return isNewMessageThreadValid;
    }
    return isMessageReplyValid;
  }, [
    draft?.draftType,
    isMessageReplyValid,
    isNewMessageThreadValid,
    isUploadingFiles,
  ]);

  return {
    createDraft,
    draftExists: draft === null ? undefined : !!draft,
    draft,
    isDraftLoaded,
    files,
    isUploadingFiles,
    hasAttachments,
    mentionIds,
    isExternal,
    bottomButtonTypes,
    isMessageReplyValid,
    isNewMessageThreadValid,
    userValues,
    error,
    canSendMessage,
    setError,
    clearQuotedMessage,
    setRelatedMessageThread,
    setForwardToMessageThread,
    setQuotedMessages,
    setReplyQuotedMessage,
    setSubject,
    setType,
    addGif,
    deleteGif,
    updateMessage,
    toggleRelatedMessageThread,
    toggleLocked,
    setIsLocked,
    setCreateMessageUserIdList,
    updateCalloutUserIds,
    copyFiles,
    setTask,
    setHappycoWorkOrderId,
    setEditMessageDraft,
    setDelta,
    deleteDraft,
  };
};

export default useMessageThreadDraft;
